<script setup>
import UmisisModal from '../../../../../../../resources/js/components/umisis/generic/UmisisModal.vue'
import { useModalControl } from '../../../../../../../resources/js/composables/modal/useUmisisModal.js'
import { computed, ref } from 'vue'
import { Field, FieldDescription, FieldLabel } from '../../../../../../../resources/js/components/ui/field/index.js'
import InputTagsWithPredefined from '../../../../../../../resources/js/components/umisis/form/inputs/InputTagsWithPredefined.vue'
import { useBusinessSystemStore } from '../../stores/businessSystems.js'
import { useApiAction } from '../../../../../../../resources/js/composables/dispatchers/useApiAction.js'
import { toast } from 'vue-sonner'
import axios from 'axios'

const { componentRef: dialog, openModal, closeModal } = useModalControl()

const props = defineProps({
  businessSystemId: {
    type: [Number, String],
    required: true,
  },
  existingUserIds: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['added'])
const businessSystemStore = useBusinessSystemStore()

const selectedUsers = ref([])
const selectedRoleId = ref(null)
const usersOptions = ref([])
const roleOptions = ref([])
const loadingOptions = ref(false)

const fieldControlBaseClass = 'flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50'
const fieldControlClass = (fieldName) => ([
  fieldControlBaseClass,
  validationResult(fieldName) === false ? 'border-destructive focus-visible:ring-destructive bg-red-50' : '',
  validationResult(fieldName) === true ? 'border-success focus-visible:ring-success bg-green-50' : '',
])

const isSubmitDisabled = computed(() => {
  return loading.value || loadingOptions.value || selectedUsers.value.length === 0 || selectedRoleId.value === null
})

const normalizeCollection = (payload) => {
  if (Array.isArray(payload)) {
    return payload
  }

  if (Array.isArray(payload?.data)) {
    return payload.data
  }

  return []
}

const onShow = async () => {
  selectedUsers.value = []
  selectedRoleId.value = null
  loadingOptions.value = true

  try {
    const [usersResponse, rolesResponse] = await Promise.all([
      axios.get('/api/v1/users', {
        params: {
          not_paginated: true
        },
      }),
      axios.get('/api/v1/user-roles', {
        params: {
          not_paginated: true
        },
      }),
    ])

    const assignedUserIds = new Set(props.existingUserIds.map((userId) => Number(userId)))
    const users = normalizeCollection(usersResponse.data)
    const roles = normalizeCollection(rolesResponse.data)

    usersOptions.value = users
      .filter((user) => !assignedUserIds.has(Number(user.id)))
      .map((user) => ({
        id: Number(user.id),
        name: `${user.name} (${user.email})`,
      }))

    roleOptions.value = roles.map((role) => ({
      id: Number(role.id),
      name: role.name,
    }))

    if (roleOptions.value.length > 0) {
      selectedRoleId.value = roleOptions.value[0].id
    }
  } finally {
    loadingOptions.value = false
  }
}

const { execute, loading, getError, validationResult } = useApiAction((payload) => {
  return businessSystemStore.addUsersToBusinessSystem(props.businessSystemId, payload)
}, {
  onSuccess: () => {
    toast.success('Users added to business system.')
    emit('added')
    closeModal()
  },
})

defineExpose({
  openModal,
  closeModal,
})

const handleOk = async (bvEvent) => {
  if (bvEvent) {
    bvEvent.preventDefault()
  }

  await execute({
    user_ids: selectedUsers.value.map((user) => Number(user.id)),
    role_id: Number(selectedRoleId.value),
  })
}
</script>

<template>
  <UmisisModal
    ref="dialog"
    title="Add Users"
    description="Select one or more users and assign the same role to all."
    ok-title="Add users"
    cancel-title="Cancel"
    :ok-disabled="isSubmitDisabled"
    @ok="handleOk"
    @show="onShow"
  >
    <div class="flex flex-col gap-4">
      <Field>
        <FieldLabel for="business-system-user-selection">
          Users
        </FieldLabel>
        <InputTagsWithPredefined
          id="business-system-user-selection"
          v-model="selectedUsers"
          :options="usersOptions"
          placeholder="Search users..."
        />
        <FieldDescription v-if="getError('user_ids')" class="text-destructive font-medium">
          {{ getError('user_ids') }}
        </FieldDescription>
      </Field>

      <Field>
        <FieldLabel for="business-system-user-role">
          Role
        </FieldLabel>
        <select
          id="business-system-user-role"
          v-model="selectedRoleId"
          :class="fieldControlClass('role_id')"
        >
          <option
            v-for="roleOption in roleOptions"
            :key="roleOption.id"
            :value="roleOption.id"
          >
            {{ roleOption.name }}
          </option>
        </select>
        <FieldDescription v-if="getError('role_id')" class="text-destructive font-medium">
          {{ getError('role_id') }}
        </FieldDescription>
      </Field>
    </div>
  </UmisisModal>
</template>
